import GameObject from '../../../GameObject/GameObject';
import AnimationClip from '../AnimationClip';
import { Easing, Tween } from '@tweenjs/tween.js';
import Renderable from '../../../Component/Renderable';
import TextComponent from '../../../Component/Render/Text';
import ElementComponent from '../../../Component/UI/Element';
import { delay } from '../../../Utility/Task/TaskQueue';

const TXT_TITLE: string = 'main_container/section_cover/section_text';
const PODIUM: string = 'main_container/podium_mask/podium_container';
const PODIUM_BRONZE: string = `${PODIUM}/podium_bronze`;
const PODIUM_SILVER: string = `${PODIUM}/podium_silver`;
const PODIUM_GOLD: string = `${PODIUM}/podium_gold`;
const SHINE: string = `${PODIUM}/player_shine`;
const CROWN: string = `${PODIUM}/player_crown`;
const WORLD_SCORE: string = 'main_container/world_score';
const TXT_STARS: string = `${WORLD_SCORE}/star_container/text`;
const TXT_CHESTS: string = `${WORLD_SCORE}/chest_container/text`;
const TXT_CARDS: string = `${WORLD_SCORE}/card_container/text`;

export class TravelMenu extends AnimationClip {
    public *execute(
        gameObject: GameObject,
        meta: {
            direction: string;
            title: string;
            stars: string;
            chests: string;
            cards: string;
        }
    ): IterableIterator<number> {
        const element = gameObject.find(PODIUM).getComponent(ElementComponent);

        if (meta.direction && meta.direction != 'none') {
            if (meta.direction === 'left') {
                this.moveLeft(element);
            } else if (meta.direction === 'right') {
                this.moveRight(element);
            }

            const title = gameObject.find(TXT_TITLE);
            this.alpha(title, 1, 0);
            const scores = gameObject.find(WORLD_SCORE);
            this.alpha(scores, 1, 0);

            yield* delay(300);

            this.swapTexts(gameObject, meta);

            this.alpha(title, 0, 1);
            this.alpha(scores, 0, 1);

            yield* delay(100);
        }

        element.layout.anchors = [0, 0, 1, 1];
        gameObject.find(PODIUM).children.forEach(child => {
            child.getComponent(Renderable).visible = false;
        });

        this.showPodium(gameObject.find(PODIUM_BRONZE));

        yield* delay(100);

        this.showPodium(gameObject.find(PODIUM_SILVER));

        yield* delay(100);

        this.showPodium(gameObject.find(PODIUM_GOLD));
        this.showShine(gameObject.find(SHINE));

        yield* delay(300);

        this.showCrown(gameObject.find(CROWN));

        while (true) yield;
    }

    private swapTexts(gameObject: GameObject, meta?: any): void {
        gameObject.find(TXT_TITLE).getComponent(TextComponent).text =
            meta.title;

        gameObject.find(TXT_STARS).getComponent(TextComponent).text =
            meta.stars;

        gameObject.find(TXT_CHESTS).getComponent(TextComponent).text =
            meta.chests;

        gameObject.find(TXT_CARDS).getComponent(TextComponent).text =
            meta.cards;
    }

    private moveLeft(podium: ElementComponent) {
        podium.layout.anchors = [0, 0, 1, 1];
        const positionTween = this.getTween(podium.layout.anchors);
        positionTween
            .to({ 0: -1, 2: 0 }, 500)
            .easing(Easing.Quadratic.Out)
            .start(this.elapsedTime);
    }

    private moveRight(podium: ElementComponent) {
        podium.layout.anchors = [0, 0, 1, 1];
        const positionTween = this.getTween(podium.layout.anchors);
        positionTween
            .to({ 0: 1, 2: 2 }, 500)
            .easing(Easing.Quadratic.Out)
            .start(this.elapsedTime);
    }

    private alpha(gameObject: GameObject, start: number, end: number) {
        const renderable = gameObject.getComponent(Renderable);
        renderable.alpha = start;

        const alphaTween = this.getTween(renderable);
        alphaTween
            .to({ alpha: end }, 300)
            .easing(Easing.Linear.None)
            .start(this.elapsedTime);
    }

    private showPodium(podium: GameObject<PIXI.Container>) {
        podium.getComponent(Renderable).visible = true;

        // Animate podium.
        const podiumElement = podium
            .find('podium')
            .getComponent(ElementComponent);
        podiumElement.layout.pivot[1] = 0;

        const podiumScaleTween = this.getTween(podiumElement.layout.pivot);
        podiumScaleTween.to({ 1: 1 }, 200).start(this.elapsedTime);

        // Animate player avatar.
        const avatarElement = podium
            .find('player_avatar')
            .getComponent(ElementComponent);
        avatarElement.layout.scale = [0, 0];

        const avatarScaleTween = this.getTween(avatarElement.layout.scale);
        avatarScaleTween.to({ 0: 1, 1: 1 }, 400).start(this.elapsedTime);

        // Animate player name.
        const nameRenderable = podium
            .find('player_name')
            .getComponent(Renderable);
        nameRenderable.alpha = 0;

        const nameFadeTween = this.getTween(nameRenderable);
        nameFadeTween
            .to({ alpha: 1 }, 400)
            .delay(200)
            .start(this.elapsedTime);
    }

    private showShine(shine: GameObject<PIXI.Container>) {
        shine.getComponent(Renderable).visible = true;
        const element = shine.getComponent(ElementComponent);
        element.layout.scale = [0, 0];
        element.layout.angle = 0;

        const scaleTween = this.getTween(element.layout.scale);
        const rotateTween = this.getTween(element.layout);
        scaleTween.chain(rotateTween);

        scaleTween.to({ 0: 1, 1: 1 }, 500).start(this.elapsedTime);

        rotateTween.to({ angle: 360 }, 5000).repeat(Infinity);
    }

    private showCrown(crown: GameObject<PIXI.Container>) {
        // Fade in.
        const renderable = crown.getComponent(Renderable);
        renderable.visible = true;
        renderable.alpha = 0;

        const alphaTween = this.getTween(renderable);
        alphaTween.to({ alpha: 1 }, 300).start(this.elapsedTime);

        // Fall out.
        const element = crown.getComponent(ElementComponent);
        element.layout.offsets[1] = 246 + 20;

        const moveTween = this.getTween(element.layout.offsets);
        moveTween
            .to({ 1: 246 }, 300)
            .easing(Easing.Bounce.Out)
            .delay(500)
            .start(this.elapsedTime);
    }
}
